import { colors } from "../colors";

export function getVenue<T extends { id: number }>(venues: T[], venueId: number): T | null {
    return venues.find(({ id }) => id === venueId) || null;
}

type GetDepartmentVenue = {
    id: number;
    departments: { id: number }[];
    archivedDepartments: { id: number }[];
};

export function getDepartment<T extends GetDepartmentVenue>(
    venues: T[],
    id: number | null | undefined
): { department: T["departments"][number] | null; venue: T | null } {
    if (id == null) {
        return { department: null, venue: null };
    }

    for (const venue of venues) {
        for (const department of [...venue.departments, ...venue.archivedDepartments]) {
            if (department.id === id) {
                return { department, venue };
            }
        }
    }
    return { venue: null, department: null };
}

type FindPositionPosition = {
    id: number;
};

type FindPositionDepartment = {
    id: number;
    positions: FindPositionPosition[];
    archivedPositions: FindPositionPosition[];
};

type FindPositionVenue = {
    id: number;
    departments: FindPositionDepartment[];
    archivedDepartments: FindPositionDepartment[];
};

export function findPositionById<T extends FindPositionVenue>(
    venues: T[],
    id: number
): {
    venue: T;
    department: T["departments"][number];
    position: T["departments"][number]["positions"][number];
} | null {
    for (const venue of venues) {
        for (const department of [...venue.departments, ...venue.archivedDepartments]) {
            const position = [...department.positions, ...department.archivedPositions].find((r) => r.id === id);
            if (position) {
                return {
                    venue,
                    department,
                    position,
                };
            }
        }
    }
    return null;
}

type GetPositionPosition = {
    id: number;
    name: string;
    color: string;
    departmentId: number;
};

type GetPositionDepartment = {
    id: number;
    name: string;
    color: string;
    positions: GetPositionPosition[];
    archivedPositions: GetPositionPosition[];
};

type GetPositionVenue = {
    id: number;
    name: string;
    departments: GetPositionDepartment[];
    archivedDepartments: GetPositionDepartment[];
};

export function getPosition<T extends GetPositionVenue>(venues: T[], archivedVenues: T[], id: number) {
    const res = findPositionById([...venues, ...archivedVenues], id);
    if (res) {
        return {
            ...res,
            label:
                venues.length > 1 // TODO: Should this be based on only active venues, not all venues?
                    ? `${res.venue.name} / ${res.department.name} / ${res.position.name}`
                    : `${res.department.name} / ${res.position.name}`,
            color: getPositionColorFromDepartment(res.position, res.department),
        };
    }

    return null;
}

export function getPositionColorFromDepartment(position: { color?: string }, department?: { color: string }) {
    if (position.color) {
        return position.color;
    }

    const color = (department && department.color) || "";
    return colors[color] || color;
}

type GetPositionColorPosition = {
    id: number;
    color: string;
    departmentId: number;
};

type GetPositionColorDepartment = {
    id: number;
    color: string;
    positions: GetPositionColorPosition[];
    archivedPositions: GetPositionColorPosition[];
};

type GetPositionColorVenue = {
    id: number;
    departments: GetPositionColorDepartment[];
    archivedDepartments: GetPositionColorDepartment[];
};

export function getPositionColor<T extends GetPositionColorVenue>(venues: T[], position: GetPositionColorPosition) {
    if (position.color) {
        return position.color;
    }
    const { department } = getDepartment(venues, position.departmentId);

    return getPositionColorFromDepartment(position, department ?? undefined);
}
